import { PlusCircleIcon } from "@heroicons/react/24/solid";
import type React from "react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { A } from "@/atoms-react/a/A.react.tsx";
import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import { Modal, ModalContent } from "@/atoms-react/modal/index.react";
import { Base, Info } from "@/atoms-react/text/Text.react";
import { EmptyPageCactusAnimation } from "@/components/animations/empty-cactus/index.react.tsx";
import { useKYCContext } from "@/features/auth/hooks/use-kyc-context";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { MandateService } from "@/features/company/types";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { usePartnerInfo } from "@/features/partner/hooks/use-partner-information";
import { Routes } from "@/features/router";
import { useDeleteAlias } from "@/features/user/hooks/use-delete-alias";
import { useUser } from "@/features/user/hooks/use-user";
import { useUserCheckAccess } from "@/features/user/hooks/use-user-check-access";
import useCreateIban from "@/features/user/iban/hooks/use-create-iban";
import { useQuiz } from "@/features/user/quiz/hooks/use-quiz";
import type { AliasIBAN, identityPartners } from "@/features/user/types";
import { NaturalEntityDataType } from "@/features/utils/types";
import { IbanCard } from "@/molecules/iban-card/index.react";
import { ModalAddIban } from "@/molecules/modal-add-iban/index.react";
import { ModalDeleteAlias } from "@/molecules/modal-delete-alias/index.react";
import { ModalIbanAccount } from "@/molecules/modal-iban-account/index.react.tsx";
import { ModalKYCIdentity } from "@/molecules/modal-kyc-identity/index.react.tsx";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setAddAlias } from "@/redux/slices/companyOnboardingSlice";
import { Themes } from "@/types/theme.ts";
import { LayoutTitle } from "../../../_layout/index.react";

type AliasesAddressProps = { isOnboarding?: boolean } & React.ComponentPropsWithoutRef<"div">;

const MAX_ATTEMPTS = 5;
const INTERVAL_CALL_ISVALIDATEIBAN = 3000;

export const AliasesPaymentApp = ({ isOnboarding = false, ...props }: AliasesAddressProps) => {
  const { t } = useTranslation(["pass"]);
  const { useUserProfile } = useUser();
  const { data: user } = useUserProfile();
  const navigate = useNavigate();
  const { companyDetailQuery } = useCompany();
  const dispatch = useAppDispatch();

  const { leUuid } = useCompanyContext();
  const { data: companyDetails } = companyDetailQuery(leUuid as string);
  const { checkPermission } = useUserCheckAccess();

  const { auditPending, kycLevel } = useKYCContext();
  const { partnerUuidQuery } = usePartnerInfo();
  const ibans = leUuid ? companyDetails?.legalEntity.AliasIBAN : user?.naturalPerson.AliasIBAN;
  const { data: dataPartner } = partnerUuidQuery(
    (sessionStorage.getItem("origin") as string) ?? null
  );
  const enabled = (() =>
    !leUuid
      ? true
      : !companyDetails?.legalEntity.UserValidation &&
        checkPermission(MandateService.ALIASES_MANAGEMENT, user, companyDetails))();
  const [searchParams] = useSearchParams();
  const allowedToKypay = (() => {
    if (user?.identityPartners) {
      const found = user?.identityPartners?.find(
        (partner: identityPartners) => partner.uuid === dataPartner?.data.partner_uuid
      );
      if (found) {
        if (leUuid) return (kycLevel as number) >= (found?.minimum_olkypass_pro_level as number);
        return (kycLevel as number) >= found?.minimum_olkypass_level;
      }
    }
  })();
  const addIbanInOnboarding = useAppSelector((state) => state.companyOnboarding.addAlias);
  const [modalAddIban, setModalAddIban] = useState<boolean>(
    !!searchParams.get("add") || addIbanInOnboarding
  );
  const [deletedIBAN, setDeletedIBAN] = useState<AliasIBAN | null>(null);
  const { mutateAsync: createIban } = useCreateIban();
  const { mutateAsync: deleteAlias } = useDeleteAlias();
  const origin = useOrigin();

  useEffect(() => {
    if (!addIbanInOnboarding) return;
    dispatch(setAddAlias(false));
  }, [addIbanInOnboarding]);

  const onDelete = async () => {
    if (!enabled) return;
    if (!deletedIBAN) {
      return;
    }
    await deleteAlias({
      ItemType: NaturalEntityDataType.AliasIBANDto,
      ItemUUID: deletedIBAN.UUID || "",
      UUID: user?.naturalPerson?.UUID || ""
    });
    setDeletedIBAN(null);
  };

  const { useIsValidatedIbanQuizMutation, useRequestValidationMutation } = useQuiz();
  const { mutateAsync: isValidatedIbanFunc } = useIsValidatedIbanQuizMutation();
  const { mutateAsync: requestIbanValidate } = useRequestValidationMutation();

  const [attemptsIsValidatedIban, setAttemptsIsValidatedIban] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [validateIbanSucceeded, setValidateIbanSucceeded] = useState<boolean | null>(null);

  const [iban, setIban] = useState<string>();

  const [isModalIbanOpen, setIsModalIbanOpen] = useState<boolean>(false);
  const [modalIdentity, setModalIdentity] = useState<boolean>(false);
  const openKYCModal = () => {
    setModalIdentity(true);
    setIsModalIbanOpen(false);
  };
  const handleValidateIban = async (iban: string) => {
    setAttemptsIsValidatedIban(0);
    setLoading(true);
    try {
      const responseIbanValidation = await requestIbanValidate(iban);
      setValidateIbanSucceeded(responseIbanValidation.data?.valid ?? null);
      setIban(iban);
      if (responseIbanValidation.data?.valid === true) {
        setLoading(false);
        setIban("");
        return;
      }
    } catch (_error) {
      setLoading(false);
      toast.error(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.european_account.error"));
    }
  };

  const onTotalDurationExceeded = async (timer: NodeJS.Timeout, uuidIban: string) => {
    clearInterval(timer);
    try {
      const response = await isValidatedIbanFunc(uuidIban);
      if (response.data?.valid) {
        setLoading(false);
        setIban("");
      } else if (response.data?.valid === false) {
        setLoading(false);
        toast.error(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.european_account.error"));
      } else {
        if (attemptsIsValidatedIban < MAX_ATTEMPTS - 1) {
          setAttemptsIsValidatedIban((prev) => prev + 1);
        } else {
          setLoading(false);
          setIban("");
          if (!response.data?.valid)
            toast.error(
              t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.european_account.error")
            );
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (validateIbanSucceeded === false && loading) {
      toast.error(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.european_account.error"));
      setLoading(false);
    } else if (loading && iban && validateIbanSucceeded === null) {
      const interval = 100;

      let elapsedTime = 0;
      timer = setInterval(() => {
        elapsedTime += interval;
        if (elapsedTime >= INTERVAL_CALL_ISVALIDATEIBAN) onTotalDurationExceeded(timer, iban);
      }, interval);
    }
    return () => clearInterval(timer);
  }, [attemptsIsValidatedIban, iban, loading, validateIbanSucceeded]);

  return (
    <Column {...props}>
      {!isOnboarding && (
        <LayoutTitle className="sm:mb-0">{t("aliasPaymentsPage.title")}</LayoutTitle>
      )}

      <div className="mb-4 flex w-full justify-start">
        <Button
          className="mt-4 w-auto sm:mt-0"
          disabled={!enabled}
          onClick={() => setModalAddIban(true)}
          icon={<PlusCircleIcon />}
        >
          {t("aliasPaymentsPage.button.add_iban")}
        </Button>
      </div>

      <ModalAddIban
        open={modalAddIban}
        onClose={() => setModalAddIban(false)}
        onValidated={async (iban, main) => {
          try {
            if (ibans?.find((ibn) => ibn.Iban === iban)) {
              toast.error(t("olkypass.accountPage.notifications.error.iban.exists"));
              return;
            }
            await createIban({ iban, main });
            toast.success(t("success.create"));
            setModalAddIban(false);
          } catch (_) {
            toast.error(t("error.create"));
          }
        }}
      />
      {deletedIBAN && (
        <>
          {deletedIBAN.valid ? (
            <Modal
              open={!!deletedIBAN}
              onClose={() => setDeletedIBAN(null)}
              {...props}
              maxWidthClassName="max-w-2xl"
            >
              <ModalContent>
                <div className={twMerge("flex flex-col justify-between")}>
                  <Base className="mt-3 flex flex-row justify-center text-center text-slate-800 text-sm">
                    {t("aliasPaymentsPage.delete_alert")}
                  </Base>
                  <div className="flex flex-row justify-center">
                    <Button
                      theme={Themes.white}
                      className="mt-5 w-2/6 border-primary text-sm"
                      onClick={() => {
                        navigate("/pass/support");
                      }}
                      size="md"
                    >
                      {t("olkypass.general.level_modal_documents.gold_form_button")}
                    </Button>
                  </div>
                </div>
              </ModalContent>
            </Modal>
          ) : (
            <ModalDeleteAlias
              isOpen={!!deletedIBAN}
              onClose={() => setDeletedIBAN(null)}
              onValidated={onDelete}
            >
              <Base>{t("aliasPaymentsPage.confirm_delete", { name: deletedIBAN?.Iban })}</Base>
            </ModalDeleteAlias>
          )}
        </>
      )}
      <div className={"flex max-w-3xl flex-col"}>
        {ibans &&
          ibans?.map((aliasIban) => (
            <IbanCard
              key={aliasIban.UUID}
              iban={aliasIban}
              disabled={!enabled}
              onDelete={() => {
                if (aliasIban.Main) return;
                setDeletedIBAN(aliasIban);
              }}
              onPublicClick={() => navigate(Routes.account_alias_public)}
              onClickHandleVerify={async () => await handleValidateIban(aliasIban.UUID)}
              loading={loading && iban === aliasIban.UUID}
            />
          ))}
      </div>
      {!ibans ||
        (ibans.length === 0 && (
          <>
            <div className="md:border-2 md:border-slate-50 dark:md:border-slate-800">
              <div className="my-4 flex items-center justify-between">
                <div className="flex w-full flex-col items-center space-y-4">
                  <EmptyPageCactusAnimation />
                  <Info className={"text-center"}>
                    {t("aliasPaymentsPage.no_iban_helper.first_part")}
                    <br />
                    {t("aliasPaymentsPage.no_iban_helper.second_part")}
                    <A
                      disabled={!enabled}
                      data-cy="button-add-iban"
                      onClick={() => setModalAddIban(true)}
                    >
                      {t("aliasPaymentsPage.no_iban_helper.link_part")}
                    </A>
                  </Info>
                </div>
              </div>
            </div>
          </>
        ))}
      {!user?.naturalPerson?.codeShop &&
        user?.naturalPerson.olkypay_account_requested !== 1 &&
        origin == FromURL.None && (
          <>
            <div className="mt-4 md:border-2 md:border-slate-50 dark:md:border-slate-800">
              <div className="my-4 flex items-center justify-between">
                <div className="flex w-full flex-col p-4">
                  <Base className="items-center font-semibold text-base text-slate-400">
                    {t("aliasPaymentsPage.open_account")}
                  </Base>
                  <Button
                    size="md"
                    theme={Themes.primary}
                    onClick={() => setIsModalIbanOpen(true)}
                    disabled={
                      (!!leUuid ||
                        auditPending ||
                        user?.naturalPerson.olkypay_account_requested === 1 ||
                        user?.naturalPerson.codeShop) as boolean
                    }
                    iconPosition="before"
                    className="mt-4 h-8 w-48 border-none text-white text-xs hover:bg-slate-50 sm:h-10 sm:w-64 sm:text-base lg:h-8 lg:w-72 lg:text-sm xl:h-10 xl:text-base xl:text-lg dark:bg-primary dark:text-white dark:hover:bg-slate-50"
                  >
                    {t("olkypass.mainPage.olkypass.bloc2.btn")}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

      <ModalIbanAccount
        allowedConsoPartner={allowedToKypay}
        isOpen={isModalIbanOpen}
        onClose={() => {
          sessionStorage.removeItem("DocumentKsyc");
          sessionStorage.removeItem("DocumentQuizKyc");
          setIsModalIbanOpen(false);
        }}
        openKYCModal={openKYCModal}
      />
      <ModalKYCIdentity onClose={() => setModalIdentity(false)} open={modalIdentity} />
    </Column>
  );
};
